import classNames from 'classnames';
import { Link } from 'core/i18n/navigation';
import { MouseEvent, ReactNode } from 'react';
import Button, { ButtonProps, ButtonSize } from 'elements/Button';
import { reportUserClickedOnEvent } from 'utils/analytics/segment';
import { useLoginModalTrigger } from 'hooks/useLoginModalTrigger';

type Variant = 'primary' | 'outline' | 'yellow' | 'gold' | 'coral';

interface Props {
  children: ReactNode;
  variant?: Variant;
  size?: ButtonSize;
  href?: string;
  withArrow?: boolean;
  /** Open the `href` in a new browser tab. */
  newTab?: boolean;
  /** Segment "location" reported when an `href` button is clicked. */
  reportLocation?: string;
  className?: string;
  onClick?: () => void;
}

const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    className="h-[17px] w-[17px]"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export const CtaButton = ({
  children,
  variant = 'primary',
  size = 'md',
  href,
  withArrow = false,
  newTab = false,
  reportLocation,
  className,
  onClick,
}: Props) => {
  const { color, brand } = variantMap[variant];
  const openLoginModal = useLoginModalTrigger(href);

  const button = (
    <Button
      type="button"
      variant={variant === 'outline' ? 'outline' : 'solid'}
      color={color}
      size={size}
      rightIcon={withArrow ? <ArrowIcon /> : undefined}
      // Brand styling wins over the base Button colors via twMerge.
      className={classNames(styles.base, brand, className)}
      onClick={onClick}
    >
      {children}
    </Button>
  );

  if (href) {
    const report = (event: MouseEvent) => {
      if (reportLocation) {
        reportUserClickedOnEvent('Get Started', reportLocation);
      }
      openLoginModal(event);
    };

    const newTabProps = newTab
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {};

    // Anchors and external URLs keep a plain <a>; internal paths use next-intl's
    // locale-aware <Link> for consistent SSR/client hrefs + soft navigation.
    if (/^https?:\/\//.test(href) || href.startsWith('#')) {
      return (
        <a href={href} onClick={report} {...newTabProps}>
          {button}
        </a>
      );
    }

    return (
      <Link href={href} onClick={report} {...newTabProps}>
        {button}
      </Link>
    );
  }

  return button;
};

const styles = {
  // Match the marketing design: pill, bold 15px, subtle lift on hover.
  base: 'gap-[9px] text-[15px] font-bold hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]',
};

// Each variant styles the light landing theme by default with `marketing-dark:`
// overrides reproducing the dark (navy) look exactly.
const variantMap: Record<
  Variant,
  { color: ButtonProps['color']; brand: string }
> = {
  primary: {
    color: 'primary',
    brand: classNames(
      'bg-mf-blue text-white hover:bg-[#3568d4]',
      'shadow-[0_2px_8px_rgba(39,87,186,0.22)]',
      'marketing-dark:shadow-[0_8px_22px_rgba(39,87,186,0.4)]',
    ),
  },
  outline: {
    color: 'neutral',
    brand: classNames(
      'border-[1.5px]',
      'bg-white border-mf-gray-300 text-mf-off-black-600 hover:bg-[#F4F4F4] hover:border-mf-gray-500',
      'marketing-dark:bg-transparent marketing-dark:border-white/35 marketing-dark:text-white',
      'marketing-dark:hover:bg-white/[0.08] marketing-dark:hover:border-white/60',
    ),
  },
  yellow: {
    color: 'warning',
    brand: 'bg-mf-yellow text-mf-off-black-600 hover:bg-[#f3c10f]',
  },
  gold: {
    color: 'warning',
    brand: classNames(
      'text-[#241200] hover:bg-[#ffb24d]',
      'bg-[#DE9628] marketing-dark:bg-mf-orange',
    ),
  },
  coral: {
    // Light theme has no coral: the band CTA is the primary blue there; the
    // dark theme keeps its coral treatment.
    color: 'neutral',
    brand: classNames(
      'bg-mf-blue text-white hover:bg-[#3568d4] shadow-[0_2px_8px_rgba(39,87,186,0.22)]',
      'marketing-dark:bg-mf-coral marketing-dark:text-mf-off-black-600 marketing-dark:hover:bg-mf-blue-300',
      'marketing-dark:shadow-none',
    ),
  },
};
